// src/private/src/pages/app/clients/components/ColumnPicker.jsx
import React, { memo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { ALL_METRIC_COLUMNS, DEFAULT_VISIBLE_COLUMNS, NO_BACKEND_VISIBLE_COLUMNS } from './dashboardUtils';

/**
 * A popover-based column picker that lets users toggle which metric columns
 * are visible in the performance tables. Persists selection to localStorage.
 */
function ColumnPicker({ visibleColumns, onChange, storageKey = 'dashboardVisibleColumns' }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback((e) => setAnchorEl(e.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleToggle = useCallback(
    (colKey) => {
      const next = new Set(visibleColumns);
      if (next.has(colKey)) {
        next.delete(colKey);
      } else {
        next.add(colKey);
      }
      onChange(next);
      try {
        localStorage.setItem(storageKey, JSON.stringify([...next]));
      } catch {
        // ignore
      }
    },
    [visibleColumns, onChange, storageKey]
  );

  const handleReset = useCallback(() => {
    onChange(new Set(DEFAULT_VISIBLE_COLUMNS));
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  }, [onChange, storageKey]);

  const handleShowAll = useCallback(() => {
    const all = new Set(ALL_METRIC_COLUMNS.map((c) => c.key));
    onChange(all);
    try {
      localStorage.setItem(storageKey, JSON.stringify([...all]));
    } catch {
      // ignore
    }
  }, [onChange, storageKey]);

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ViewColumnIcon />}
        onClick={handleOpen}
        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
      >
        Columns ({visibleColumns.size}/{ALL_METRIC_COLUMNS.length})
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, maxHeight: 420, width: 280 } } }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Toggle columns
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <Button size="small" onClick={handleReset} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
            Default
          </Button>
          <Button size="small" onClick={handleShowAll} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
            Show all
          </Button>
        </Stack>
        <Stack spacing={0}>
          {ALL_METRIC_COLUMNS.map((col) => (
            <FormControlLabel
              key={col.key}
              control={
                <Checkbox
                  size="small"
                  checked={visibleColumns.has(col.key)}
                  onChange={() => handleToggle(col.key)}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  {col.label}
                </Typography>
              }
              sx={{ mx: 0, '& .MuiCheckbox-root': { py: 0.25 } }}
            />
          ))}
        </Stack>
      </Popover>
    </>
  );
}

/**
 * Load persisted column selection from localStorage.
 */
export function loadVisibleColumns(storageKey = 'dashboardVisibleColumns', hasBackendData = true) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr) && arr.length > 0) {
        return new Set(arr);
      }
    }
  } catch {
    // ignore
  }
  return new Set(hasBackendData ? DEFAULT_VISIBLE_COLUMNS : NO_BACKEND_VISIBLE_COLUMNS);
}

export default memo(ColumnPicker);
